import { useEffect, useMemo, useState } from "react";

const C = {
  overlay: "rgba(0,0,0,0.7)",
  white: "#FFFFFF",
  gold: "#C09A5B",
  taupe: "#8B7361",
  ink: "#3D2E20",
  soft: "#F7F3EE",
  border: "#E7E1D8",
};

function YomaLogo() {
  return (
    <svg width="160" height="160" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g fill="#A89278" fontFamily="Didot, 'Bodoni MT', 'Times New Roman', serif" fontWeight="400">
        <text x="160" y="190" fontSize="110" letterSpacing="10">Y</text>
        <text x="255" y="190" fontSize="110" letterSpacing="10">O</text>
        <line x1="125" y1="230" x2="375" y2="230" stroke="#A89278" strokeWidth="2" />
        <text x="155" y="335" fontSize="110" letterSpacing="10">M</text>
        <text x="285" y="335" fontSize="110" letterSpacing="10">A</text>
      </g>
    </svg>
  );
}

function IconBuilding() {
  return (
    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-3h6v3M8 10h2m4 0h2m-6 4h2m4 0h2" stroke={C.taupe} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IconDocument() {
  return (
    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 3h7l5 5v13H7zM14 3v6h5M10 13h6M10 17h6" stroke={C.gold} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IconChecklist() {
  return (
    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M8 7h9M8 12h9M8 17h9M4.5 7l1.5 1.5L7.5 6M4.5 12l1.5 1.5L7.5 11M4.5 17l1.5 1.5L7.5 16" stroke={C.taupe} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IconStar() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m12 3 2.7 5.5L21 9.4l-4.5 4.4 1.1 6.2L12 17.1 6.4 20l1.1-6.2L3 9.4l6.3-.9L12 3z" stroke={C.gold} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

const STEP_COUNT = 5;

export default function OnboardingModal({ onClose, userName = "" }) {
  const [isVisible, setIsVisible] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    try {
      const hasSeenOnboarding = localStorage.getItem("yoma_onboarding_done");
      setIsVisible(!(hasSeenOnboarding === "true"));
    } catch {
      setIsVisible(true);
    }
  }, []);

  const firstName = useMemo(() => {
    const s = String(userName || "").trim();
    if (!s) return "";
    return s.split(/\s+/)[0];
  }, [userName]);

  function finish(destination) {
    try {
      localStorage.setItem("yoma_onboarding_done", "true");
    } catch {
      // no-op
    }
    if (onClose) onClose(destination);
    setIsVisible(false);
  }

  if (!isVisible) return null;

  const canPrev = step > 1;
  const canNext = step < STEP_COUNT;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: C.overlay,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 600,
          background: C.white,
          borderRadius: 16,
          border: `1px solid ${C.border}`,
          boxShadow: "0 24px 70px rgba(0,0,0,0.28)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "16px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {Array.from({ length: STEP_COUNT }, (_, i) => {
              const n = i + 1;
              const active = n === step;
              const done = n < step;
              return (
                <div
                  key={n}
                  style={{
                    width: active ? 22 : 8,
                    height: 8,
                    borderRadius: 99,
                    transition: "all .25s ease",
                    background: active || done ? C.gold : "#DDD3C7",
                  }}
                />
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => finish()}
            style={{
              border: "none",
              background: "transparent",
              color: C.taupe,
              fontSize: 12,
              cursor: "pointer",
              letterSpacing: ".04em",
            }}
          >
            Passer
          </button>
        </div>

        <div style={{ padding: "26px 28px 24px", minHeight: 380 }}>
          <div
            key={step}
            style={{
              animation: "yomaOnboardingFade .28s ease",
            }}
          >
            {step === 1 && (
              <div style={{ textAlign: "center" }}>
                <div style={{ marginBottom: 10, display: "flex", justifyContent: "center" }}><YomaLogo /></div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, color: C.ink, marginBottom: 8 }}>
                  Bienvenue sur YOMA{firstName ? `, ${firstName}` : ""}
                </div>
                <div style={{ color: C.taupe, fontSize: 15, marginBottom: 14 }}>
                  La plateforme des professionnels de l'espace
                </div>
                <div style={{ color: C.taupe, fontSize: 14, lineHeight: 1.7, maxWidth: 440, margin: "0 auto 24px" }}>
                  Découvrez en 4 étapes comment YOMA va transformer votre façon de travailler.
                </div>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  style={{
                    border: "none",
                    background: C.gold,
                    color: C.white,
                    padding: "12px 20px",
                    borderRadius: 10,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Commencer la visite
                </button>
              </div>
            )}

            {step === 2 && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <IconBuilding />
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: C.ink }}>Gérez vos prospects</div>
                </div>
                <div style={{ color: C.taupe, lineHeight: 1.7, marginBottom: 18 }}>
                  Qualifiez vos prospects, analysez leur projet et prenez des décisions GO/NO-GO documentées.
                </div>
                <div style={{ background: C.soft, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", color: C.taupe, marginBottom: 8 }}>Aperçu prospect</div>
                  <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, padding: 10 }}>
                    <div style={{ fontWeight: 700, color: C.ink, marginBottom: 4 }}>Villa Agdal — Rabat</div>
                    <div style={{ fontSize: 13, color: C.taupe }}>Statut: Préqualification · Budget estimé: 2 800 000 MAD</div>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <IconDocument />
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: C.ink }}>Générez vos contrats en 1 clic</div>
                </div>
                <div style={{ color: C.taupe, lineHeight: 1.7 }}>
                  Sélectionnez votre type de mission, YOMA génère automatiquement le contrat adapté avec les bonnes clauses de protection.
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <IconChecklist />
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: C.ink }}>Pilotez vos missions étape par étape</div>
                </div>
                <div style={{ color: C.taupe, lineHeight: 1.7 }}>
                  5 types de missions disponibles : Architecture Intérieure, OPC, Mandataire, AMO, Conducteur de Travaux.
                  Chaque étape se déverrouille après validation.
                </div>
              </div>
            )}

            {step === 5 && (
              <div style={{ textAlign: "center" }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}><IconStar /></div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: C.ink, marginBottom: 10 }}>Vous êtes prêt !</div>
                <div style={{ color: C.taupe, lineHeight: 1.7, maxWidth: 460, margin: "0 auto 22px" }}>
                  Commencez par créer votre premier projet ou configurer votre cabinet dans OUTILS → Branding.
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => finish("projets")}
                    style={{
                      border: "none",
                      background: C.gold,
                      color: C.white,
                      padding: "12px 18px",
                      borderRadius: 10,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Créer mon premier projet
                  </button>
                  <button
                    type="button"
                    onClick={() => finish("branding-settings")}
                    style={{
                      border: `1px solid ${C.border}`,
                      background: "transparent",
                      color: C.ink,
                      padding: "12px 18px",
                      borderRadius: 10,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Configurer mon cabinet
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {step < 5 && (
          <div style={{ borderTop: `1px solid ${C.border}`, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => canPrev && setStep((s) => s - 1)}
              disabled={!canPrev}
              style={{
                border: `1px solid ${C.border}`,
                background: "transparent",
                color: C.ink,
                padding: "10px 14px",
                borderRadius: 10,
                fontWeight: 600,
                cursor: canPrev ? "pointer" : "not-allowed",
                opacity: canPrev ? 1 : 0.45,
              }}
            >
              Précédent
            </button>
            <div style={{ display: "flex", gap: 7 }}>
              {Array.from({ length: STEP_COUNT }, (_, i) => (
                <div key={i} style={{ width: 6, height: 6, borderRadius: 99, background: i + 1 <= step ? C.gold : "#DDD3C7" }} />
              ))}
            </div>
            <button
              type="button"
              onClick={() => canNext && setStep((s) => s + 1)}
              disabled={!canNext}
              style={{
                border: "none",
                background: C.gold,
                color: C.white,
                padding: "10px 14px",
                borderRadius: 10,
                fontWeight: 700,
                cursor: canNext ? "pointer" : "not-allowed",
                opacity: canNext ? 1 : 0.45,
              }}
            >
              Suivant
            </button>
          </div>
        )}
      </div>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes yomaOnboardingFade {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
